/* eslint-disable react/require-default-props */

import { css } from '@emotion/core';
import React, { useEffect } from 'react';
import t from 'react-translate';
import { gray4, primary } from 'styles/global_defaults/colors';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import NvTooltip from 'shared/components/nv-tooltip';

interface InfoIconProps {
  infoText: string;
}
function InfoIcon(props: InfoIconProps) {
  return (
    <NvTooltip text={props.infoText}>
      <span
        css={css`
          font-size: 10px;
          color: ${primary};
          margin-left: 4px;
        `}
        className='icon-info'
      />
    </NvTooltip>
  );
}

type ReactInput = React.ComponentProps<'input'>;
export interface ColorInputProps {
  label?: string;

  // shows a little info icon next to the label with a tooltip with the infoText
  infoText?: string;
  name: string;
  value: string;
  onChange: ReactInput['onChange'];
  onBlur?: ReactInput['onBlur'];
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  onFocus?: ReactInput['onFocus'];
  showPalette?(): void;

  // use native DOM onChange
  onValueChange?(e: Event): void;
}

const rightSideButtonStyle = css`
  /* center vertically */
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  /* right margin */
  right: ${halfSpacing}px;
  /* circle style */
  border: 1px solid ${gray4};
  width: ${standardSpacing}px;
  height: ${standardSpacing}px;
  border-radius: 50%;

  &:focus {
    outline: none;
  }
`;

/* @ngInject */
export default function ColorInput(props: ColorInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { onValueChange } = props;

  useEffect(() => {
    const inputElement = inputRef.current;
    inputElement.addEventListener('change', onValueChange);

    return () => {
      inputElement.removeEventListener('change', onValueChange);
    };
  }, [onValueChange]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit?.(event);
      }}
    >
      {props.label && (
      <label className='gray-label d-block mb-1' htmlFor={props.name}>
        {props.label}
        {props.infoText && <InfoIcon infoText={props.infoText} />}
      </label>
      )}
      <div className='position-relative'>
        <input
          ref={inputRef}
          className='form-control'
          name={props.name}
          value={props.value}
          onChange={(event) => {
            // validate color
            // this regex will match the `#` followed by 0 to 6 hex digits
            // it's not case-sensitive. An empty string is valid too.
            if (event.target.value.match(/^(#[\da-f]{0,6})?$/i)) {
              props.onChange(event);
            }
          }}
          onFocus={props.onFocus}
        />
        <NvTooltip text={t.LECTURE_PAGES.COMPONENTS.COLOR_PICKER.COLOR_INPUT_RIGHT_HAND_BUTTON_TOOLTIP()}>
          <button
            type='button'
            aria-label={t.LECTURE_PAGES.COMPONENTS.COLOR_PICKER.COLOR_INPUT_RIGHT_HAND_BUTTON_TOOLTIP()}
            css={css`
              background-color: ${props.value};
              ${rightSideButtonStyle};
            `}
            onClick={props.showPalette}
          />
        </NvTooltip>
      </div>
    </form>
  );
}
